import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import {
  Input,
  MaskedInput,
  Modal,
  ModalCloseButton,
  Select,
} from 'components/Shared';
import { MODAL_DISMISS_ACTION } from 'constants/Common';
import { Order } from 'contracts/Orders';
import { useSchedulingDocks, useValidation } from 'hooks';
import {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import { UpdateOrderActions as MainActions } from 'store/ducks/orders';
import { UpdateOrderValidator as Validator } from 'validators/Orders';
import * as S from './styles';
import { useDebounce } from 'hooks/useDebounce';
import { format, isValid } from 'date-fns';
import { FindSchedulingDocks } from 'contracts/Scheduling';
import { Formatter } from 'utils';

export interface Ref {
  openModal: () => void;
}

interface Props {
  order: Order;
  onUpdate?: () => void;
}

interface IUpdateModal
  extends ForwardRefExoticComponent<Props & RefAttributes<Ref>> {}

const UpdateOrderModal: IUpdateModal = forwardRef((props, ref) => {
  const { order, onUpdate } = props;
  const [isOpen, setIsOpen] = useState(false);

  console.log('order update', order)

  const [query, setQuery] = useState<FindSchedulingDocks>({
    carrierId: order.carrierId,
    companyId: order.companyId,
    duration: order.duration,
    operationTypeId: order.operationTypeId,
    warehouseId: order.warehouseId,
    orderTypeId: order.orderTypeId,
    date: order.scheduledAt,
  });

  const formRef = useRef<FormHandles>(null);
  const debouncedQuery = useDebounce<FindSchedulingDocks>(query, 700);

  const dispatch: AppDispatch = useDispatch();

  const { handleFormErrors, handleApiErrors } = useValidation();

  const {
    schedulingDocksOptions,
    loadingSchedulingDocks,
    resetSchedulingDocks,
    fetchSchedulingDocks,
  } = useSchedulingDocks();

  const { loading: updatingOrder, validationErrors } = useSelector(
    (state: RootState) => state.updateOrder
  );

  const onDateChange = useCallback(
    ({ target: { value: date } }: React.ChangeEvent<HTMLInputElement>) => {
      if (!isValid(new Date(date))) return;

      formRef.current?.setFieldValue('dockAndTimeFrame', undefined);
      setQuery((prev) => ({ ...prev, date }));
      resetSchedulingDocks();
    },
    []
  );

  const onModalClose = useCallback((): void => {
    setIsOpen(false);
    dispatch(MainActions.reset());
    formRef.current?.setErrors({});
    formRef.current?.reset();
  }, [dispatch]);

  const setFormValues = useCallback((): void => {
    if (!isOpen) return;

    const {
      driverName,
      driverDocument,
      vehiclePlate,
      cellPhone,
      scheduledAt,
      dock,
      orderItems,
    } = order;

    formRef.current?.setData({
      driverName,
      driverDocument,
      vehiclePlate,
      cellPhone,
      date: Formatter.date(scheduledAt, { format: 'yyyy-MM-dd' }),
      // Preenche os documentos por item para permitir correção linha a linha.
      items: orderItems.map(({ document }) => ({ document })),
    });

    if (dock) {
      const time = Formatter.date(scheduledAt, { format: 'HH:mm' });

      formRef.current?.setFieldValue('dockAndTimeFrame', {
        label: `${dock.name} às ${time}`,
        value: `${dock.id}|${scheduledAt}`,
      });
    }
  }, [isOpen, order]);

  const onSuccess = useCallback((): void => {
    formRef.current?.reset();
    onModalClose();
    onUpdate?.();
  }, [onModalClose, onUpdate]);

  const onSubmit = useCallback(
    async (data: any): Promise<void> => {
      try {
        formRef.current?.setErrors({});
        const { schema } = new Validator();

        const validData = await schema.validate(data, {
          abortEarly: false,
        });

        const [dockId, scheduledAt] = validData.dockAndTimeFrame.split('|');

        // Mantém o id original para a API atualizar somente o documento do item.
        const items = validData.items?.map((item, index) => ({
          id: order.orderItems[index].id,
          document: item.document,
        }));

        Object.assign(validData, { dockId, scheduledAt, items });

        dispatch(MainActions.request(order.id, validData, onSuccess));
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [dispatch, handleFormErrors, onSuccess, order.id, order.orderItems]
  );

  const getDocks = useCallback(() => {
    const queryIsComplete = Object.values(debouncedQuery).every(Boolean);
    queryIsComplete && fetchSchedulingDocks(debouncedQuery);
  }, [debouncedQuery]);

  useEffect(() => {
    setFormValues();
  }, [setFormValues]);

  useEffect(() => {
    handleApiErrors(validationErrors, formRef);
  }, [handleApiErrors, validationErrors]);

  useEffect(() => {
    return () => {
      dispatch(MainActions.reset());
    };
  }, [dispatch]);

  useImperativeHandle(
    ref,
    () => ({
      openModal: (): void => {
        setIsOpen(true);
        getDocks();
      },
    }),
    [getDocks]
  );

  useEffect(() => {
    getDocks();
  }, [getDocks]);

  return (
    <Modal isOpen={isOpen} onClickOutside={onModalClose}>
      <S.ModalContent style={{ maxWidth: '720px' }}>
        <S.ModalHeader>
          <h4>Editar Agendamento</h4>
          <ModalCloseButton onClick={onModalClose} />
        </S.ModalHeader>
        <S.ModalBody>
          <Form ref={formRef} onSubmit={onSubmit}>
            <S.FormRow>
              <Input name="driverName" label="Nome do motorista" />
              <MaskedInput
                name="cellPhone"
                label="Celular"
                mask="(99) 99999-9999"
              />
            </S.FormRow>
            <S.FormRow>
              <MaskedInput
                name="driverDocument"
                label="CPF do motorista"
                mask="999.999.999-99"
              />
              <Input name="vehiclePlate" label="Placa do veículo" />
            </S.FormRow>
            <S.FormRow>
              <Input
                name="date"
                type="date"
                label="Data"
                onChange={onDateChange}
                min={format(new Date(), 'yyyy-MM-dd')}
              />
              <Select
                name="dockAndTimeFrame"
                label="Doca"
                options={schedulingDocksOptions}
                isLoading={loadingSchedulingDocks}
              />
            </S.FormRow>
            {/* Cada item da ordem recebe seu próprio campo de documento. */}
            {order.orderItems.map((item, index) => (
              <S.FormRow key={item.id}>
                <Input
                  name={`items[${index}].document`}
                  label={
                    order.orderItems.length > 1
                      ? `Documento ${index + 1}`
                      : 'Documento'
                  }
                />
              </S.FormRow>
            ))}
            <S.FormActions>
              <S.Button type="button" mood="light" onClick={onModalClose}>
                {MODAL_DISMISS_ACTION}
              </S.Button>
              <S.Button type="submit">
                {updatingOrder ? <S.ActivityIndicator /> : 'Salvar'}
              </S.Button>
            </S.FormActions>
          </Form>
        </S.ModalBody>
      </S.ModalContent>
    </Modal>
  );
});

export default UpdateOrderModal;
